import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CheckCircle, Loader2, Send } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiry_type: 'general',
    message: '',
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInquiryChange = (value) => {
    setFormData((prev) => ({ ...prev, inquiry_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        inquiry_type: 'general',
        message: '',
      });
    } catch (error) {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-[#F8F9FA] rounded-lg p-8 text-center" data-testid="contact-success">
        <CheckCircle className="mx-auto text-[#27AE60] mb-4" size={48} />
        <h3 className="font-['Montserrat'] font-bold text-xl text-[#2C3E50] mb-2">
          Message Sent!
        </h3>
        <p className="text-[#64748B]">
          Thank you for reaching out. I'll get back to you within 24-48 hours.
        </p>
        <Button
          onClick={() => setStatus('idle')}
          className="mt-6 bg-[#3498DB] hover:bg-[#2980B9] text-white"
          data-testid="contact-send-another"
        >
          Send Another Message
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-[#2C3E50] font-medium">
            Name *
          </Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your name"
            required
            className="border-slate-200 focus:border-[#3498DB]"
            data-testid="contact-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-[#2C3E50] font-medium">
            Email *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            required
            className="border-slate-200 focus:border-[#3498DB]"
            data-testid="contact-email-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="company" className="text-[#2C3E50] font-medium">
            Company
          </Label>
          <Input
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder="Your company"
            className="border-slate-200 focus:border-[#3498DB]"
            data-testid="contact-company-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="inquiry_type" className="text-[#2C3E50] font-medium">
            Inquiry Type
          </Label>
          <Select value={formData.inquiry_type} onValueChange={handleInquiryChange}>
            <SelectTrigger className="border-slate-200 focus:border-[#3498DB]" data-testid="contact-inquiry-select">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="consulting">Consulting Inquiry</SelectItem>
              <SelectItem value="speaking">Speaking Request</SelectItem>
              <SelectItem value="general">General Question</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="text-[#2C3E50] font-medium">
          Message *
        </Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell me about your project or question..."
          required
          rows={5}
          className="border-slate-200 focus:border-[#3498DB] resize-none"
          data-testid="contact-message-input"
        />
      </div>

      {status === 'error' && (
        <p className="text-red-500 text-sm" data-testid="contact-error">{errorMessage}</p>
      )}

      <Button
        type="submit"
        disabled={status === 'loading'}
        className="w-full md:w-auto bg-[#3498DB] hover:bg-[#2980B9] text-white shadow-[0_4px_14px_0_rgba(52,152,219,0.39)]"
        data-testid="contact-submit"
      >
        {status === 'loading' ? (
          <>
            <Loader2 className="animate-spin mr-2" size={18} />
            Sending...
          </>
        ) : (
          <>
            <Send size={18} className="mr-2" />
            Send Message
          </>
        )}
      </Button>
    </form>
  );
};
